import styles from './styles.module.css'

import { Text } from '@radix-ui/themes'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  provideRightsDataFeedback,
  type RightsDataSearchParams,
} from '@disco/api/rights-data'
import { useSearchRightsDataQuery } from '@disco/state-management/api/rights-data'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import type { TerritoryTis } from '@disco/types/types/rights-data'

import { RightsDataTable } from 'components/common/tracks/metadata-fields/rights-data/rights-data-table'
import {
  Box,
  Button,
  DiscoCallout,
  Flex,
  Heading,
  SearchTextField,
} from 'components/ds'
import { DiscoModal } from 'components/ui'
import { rightsDataModalStyles } from 'components/utils/modalStyles'

import { useSelector } from 'hooks/use-redux'

import type { NoResultsFormInputs } from './no-results-form'
import NoResultsForm from './no-results-form'
import type { RightsDataUsageFeedback } from './rights-data-feedback'
import RightsDataFeedback from './rights-data-feedback'
import { RightsLoading } from './rights-loading'
import { TERRITORIES, TERRITORY_BY_TIS } from './territories'
import TerritoryFilter from './territory-filter'

type Props = {
  handleClose: () => any
  isrc?: string
  title?: string
  artist?: string
}

const ALL_TIS = TERRITORIES.map(t => t.tis)
const KNOWN_TIS = new Set<string>(ALL_TIS)
const isKnownTis = (tis: string): tis is TerritoryTis => KNOWN_TIS.has(tis)

const TERRITORY_FILTER_STORAGE_KEY = 'rights-data-territory-filter'

const RightsDataModal = (props: Props) => {
  const constanceSettings = useSelector(getConstanceSettings)
  const rightsDataEnabled = constanceSettings?.THIRD_PARTY_RIGHTS_DATA || false

  const initialParams = () => ({
    ...(props.isrc ? { isrc: props.isrc } : {}),
    ...(props.title && props.artist
      ? { title: props.title, artist: props.artist }
      : {}),
  })

  const [searchParams, setSearchParams] =
    useState<RightsDataSearchParams>(initialParams)
  const [formInputs, setFormInputs] = useState<NoResultsFormInputs>({
    isrc: props.isrc ?? '',
    title: props.title ?? '',
    artist: props.artist ?? '',
  })

  const hasValidParams = !!(
    searchParams.isrc ||
    (searchParams.title && searchParams.artist)
  )

  const {
    currentData: rightsData,
    isFetching: isLoading,
    error: dataError,
  } = useSearchRightsDataQuery(searchParams, {
    skip: !rightsDataEnabled || !hasValidParams,
  })

  const [appliedTis, setAppliedTis] = useState<Set<TerritoryTis>>(() => {
    try {
      const saved = localStorage.getItem(TERRITORY_FILTER_STORAGE_KEY)
      if (saved) return new Set(JSON.parse(saved) as TerritoryTis[])
    } catch (_e) {
      // localStorage unavailable
    }
    return new Set(ALL_TIS)
  })
  const [feedback, setFeedback] = useState<RightsDataUsageFeedback>({
    isHelpful: null,
    comment: '',
  })

  const handleFeedbackChange = useCallback(
    (newFeedback: RightsDataUsageFeedback) => {
      setFeedback(newFeedback)
    },
    [],
  )

  const handleSubmitAndClose = useCallback(() => {
    if (feedback.isHelpful !== null && rightsData) {
      provideRightsDataFeedback(
        feedback.isHelpful,
        feedback.comment,
        rightsData?.rights_usage_id,
      )
    }
    props.handleClose()
  }, [feedback, props, rightsData])

  const handleTerritoryChange = useCallback((next: Set<TerritoryTis>) => {
    setAppliedTis(next)
    try {
      localStorage.setItem(
        TERRITORY_FILTER_STORAGE_KEY,
        JSON.stringify([...next]),
      )
    } catch (_e) {
      // localStorage unavailable
    }
  }, [])

  const availableTis = useMemo<Set<TerritoryTis>>(() => {
    if (!rightsData?.splits_by_territory) return new Set()
    return new Set(
      Object.entries(rightsData.splits_by_territory)
        .filter(
          ([tis, splits]) => isKnownTis(tis) && splits && splits.length > 0,
        )
        .map(([tis]) => tis as TerritoryTis),
    )
  }, [rightsData])

  const displayedTis = useMemo<TerritoryTis[]>(
    () => ALL_TIS.filter(tis => appliedTis.has(tis) && availableTis.has(tis)),
    [appliedTis, availableTis],
  )

  const feedbackGiven = feedback.isHelpful !== null
  const buttonText = feedbackGiven ? 'Submit feedback and Close' : 'Close'

  const is404 = dataError && 'status' in dataError && dataError.status === 404
  const noData = (rightsDataEnabled && !hasValidParams) || !!is404
  const failed = !!dataError && !is404

  const [formMode, setFormMode] = useState(false)
  useEffect(() => {
    if (is404) {
      setFormMode(true)
    } else if (rightsData) {
      setFormMode(false)
    }
  }, [is404, rightsData])

  const showForm = formMode || !!is404

  const handleTryAgain = useCallback(() => {
    const next: RightsDataSearchParams = {
      ...(formInputs.isrc.trim() ? { isrc: formInputs.isrc.trim() } : {}),
      ...(formInputs.title.trim() && formInputs.artist.trim()
        ? {
            title: formInputs.title.trim(),
            artist: formInputs.artist.trim(),
          }
        : {}),
    }
    setSearchParams(next)
    setFeedback({ isHelpful: null, comment: '' })
  }, [formInputs])

  const getPlaceholderText = () => {
    const query = rightsData?.query
    const sentBoth =
      searchParams.isrc && searchParams.title && searchParams.artist

    if (sentBoth) {
      if (query && 'isrc' in query) {
        return `Searched by ISRC: ${query.isrc}`
      }

      if (query && 'title' in query) {
        return `Searched by ISRC: ${searchParams.isrc} (no data). Searched by Title: ${query.title}, Artist: ${query.artist}`
      }

      return `Searched by ISRC: ${searchParams.isrc}`
    }

    if (searchParams.isrc) {
      return `Searched by ISRC: ${searchParams.isrc}`
    }

    if (searchParams.title && searchParams.artist) {
      return `Searched by Title: ${searchParams.title}, Artist: ${searchParams.artist}`
    }

    return 'Searching for rights data for track'
  }

  const noResultsISRC =
    'This can happen if the ISRC is incorrect. In some cases, there may simply be no third-party data available for this track.'
  const noResultsTitleArtist =
    'This can happen for a few reasons - for example, the title or artist might contain spelling errors or extra text. In some cases, there may simply be no third-party data available for this track.'
  const noResultsText = searchParams.isrc ? noResultsISRC : noResultsTitleArtist

  const calloutText = `We weren't able to return any results for this search. ${noResultsText} Edit the fields below and try again.`

  const dataBehindFilter =
    rightsData && displayedTis.length === 0 && availableTis.size > 0

  return (
    <DiscoModal
      onRequestClose={props.handleClose}
      style={rightsDataModalStyles}
    >
      <Heading mr="64px">
        <Text size="5">Rights Data Lookup</Text>
      </Heading>
      {!rightsDataEnabled ? (
        <DiscoCallout type="error">
          Rights data feature is not enabled.
        </DiscoCallout>
      ) : (
        <>
          <SearchTextField
            mb="4"
            disabled={true}
            placeholder={getPlaceholderText()}
          />
          {isLoading && !showForm && <RightsLoading />}
          {failed && (
            <DiscoCallout type="error">
              Something went wrong. Please try searching again later.
            </DiscoCallout>
          )}
          {showForm && (
            <NoResultsForm
              inputs={formInputs}
              isLoading={isLoading}
              calloutText={calloutText}
              onChange={setFormInputs}
              onTryAgain={handleTryAgain}
              onClose={handleSubmitAndClose}
            />
          )}

          {/* edge case, if no isrc or title or artist is provided */}
          {!isLoading && !failed && !showForm && noData && !rightsData && (
            <DiscoCallout type="neutral">
              We weren&apos;t able to return any results for this search.{' '}
              {noResultsText}
            </DiscoCallout>
          )}

          {rightsData && (
            <>
              <DiscoCallout type="info">
                The rights information provided below has been obtained from
                third-party sources, and is provided in order to supplement your
                search. DISCO does not guarantee that these results are either
                complete or up to date. Please double-check the details before
                relying on them for legal or licensing purposes.
              </DiscoCallout>
              <Flex justify="between" align="center" wrap="wrap" gap="3" my="2">
                <TerritoryFilter
                  appliedTis={appliedTis}
                  availableTis={availableTis}
                  onChange={handleTerritoryChange}
                />
                {rightsData.iswc && (
                  <Text size="2" weight="bold">
                    ISWC: {rightsData.iswc}
                  </Text>
                )}
              </Flex>
              {dataBehindFilter ? (
                <DiscoCallout type="neutral" my="3">
                  Rights information is available. Select one or more countries
                  above to view.
                </DiscoCallout>
              ) : (
                <Flex direction="column" className={styles.tableArea}>
                  {displayedTis.map(tis => (
                    <RightsDataTable
                      key={tis}
                      territory={{
                        code: TERRITORY_BY_TIS[tis].code,
                        name: TERRITORY_BY_TIS[tis].name,
                      }}
                      splits={rightsData.splits_by_territory[tis] ?? []}
                      allWriters={rightsData.all_writers}
                      master={rightsData.master}
                    />
                  ))}
                </Flex>
              )}

              <RightsDataFeedback onFeedbackChange={handleFeedbackChange} />
            </>
          )}
        </>
      )}
      {!showForm && (
        <Box>
          <Flex justify="end" mt="2">
            <Button
              color="gray"
              highContrast
              size="2"
              onClick={handleSubmitAndClose}
            >
              {buttonText}
            </Button>
          </Flex>
        </Box>
      )}
    </DiscoModal>
  )
}

export default RightsDataModal
